import { Injectable, inject, signal } from "@angular/core";
import { FilterTag, Sidebar, SidebarCheckList, SidebarFilterTypes, SidebarMenu } from './sidebar.types';
import { goToTopPage, splitByUpperCase } from "../common/utils";
import { MenuService } from "../menu.service";
import { SignalsStoreService } from "../signals-store.service";
import { ProductResume } from "../../product/product.types";
import { toObservable } from "@angular/core/rxjs-interop";
import { Item, Menu } from '../types/common.types';

@Injectable({
  providedIn: 'root'
})
export class SidebarService {

  //#region Services

  #menuService = inject(MenuService);

  #signalsStoreService = inject(SignalsStoreService);

  //#endregion

  //#region Properties

  #allSubcategoriesMenu = signal<SidebarMenu[]>([]);

  sidebar = signal<SidebarCheckList[]>([]);

  sidebarMenu = signal<SidebarMenu[]>([]);

  filterTags = signal<FilterTag[]>([]);

  //#endregion

  //#region Constructor

  constructor() {
    toObservable(this.#signalsStoreService.selectedCategory)
      .subscribe(x => x.id !== -1 && this.setUpMenu());
  }

  //#endregion

  //#region Methods

  setUpSidebarData(data: Sidebar | any) {

    const sidebar: SidebarCheckList[] = [];
    const arr = [SidebarFilterTypes.producers, SidebarFilterTypes.dietRestrictions, SidebarFilterTypes.tags];

    for (const id in data) {
      if (Object.prototype.hasOwnProperty.call(data, id)) {

        const key = id.toLowerCase();

        if (!arr.includes(key.toLocaleLowerCase() as SidebarFilterTypes))
          continue;

        const element = data[key];

        if (!element?.length)
          continue;

        sidebar.push({
          id: key,
          key: `${key}-`,
          name: splitByUpperCase(id).join(' ').toLowerCase(),
          checked: false,
          filters: new Map(),
          countActiveFilters: 0,
          sublist: element.map((e: any) => {
            e.name = e.name.trim()
            e.disabled = false;
            e.checked = false;
            e.countActiveFilters = 0;
            return e;
          })
        });
      }
    }

    data.tagsCategories
      .forEach((x: any) => {

        sidebar.push({
          id: x.id.toString(),
          key: `${SidebarFilterTypes.tags}-${x.id}`,
          name: x.name,
          checked: false,
          filters: new Map(),
          countActiveFilters: 0,
          sublist: x.tags.map((e: any) => {
            e.name = e.name.trim()
            e.disabled = false;
            e.checked = false;
            e.countActiveFilters = 0;
            return e;
          })
        });
      });

    this.sidebar.set(this.#orderCategories(sidebar));
  }

  #orderCategories(sidebar: SidebarCheckList[]) {

    sidebar.forEach(item => {
      if (Array.isArray(item.sublist)) {
        item.sublist.sort((a, b) => a.name.localeCompare(b.name));
      }
    });

    const order = ["producers", "Dietary", "Production", "Packaging", "Mileage", "Ownership"];
    order.push(...sidebar.map(x => x.name).filter(x => !order.includes(x)));

    const sort = order.map(name =>
      sidebar.find(item => item.name === name)
    ).filter(Boolean);

    return sort.filter<SidebarCheckList>(x => !!x);
  }

  setUpMenu() {
    this.#allSubcategoriesMenu.set([]);
    this.sidebarMenu.set([]);

    if (!this.#menuService.menu())
      return;

    const category = this.#menuService
      .menu()
      .find(x => x.id === this.#signalsStoreService.selectedCategory().id);

    if (!category)
      return;

    const subcategory = this.#setUpMenuItem(category, category.id.toString());

    this.#allSubcategoriesMenu.set([subcategory]);
    this.sidebarMenu.set([subcategory]);
  }

  setUpMenuFilter(categories: Item[], subcategories: Item[], subcategoriesSon: Item[]) {

    let menu = structuredClone(this.#menuService.menu());

    if (categories.length)
      menu = menu.filter(x => categories.map(y => y.id).includes(x.id));

    if (subcategories.length) {
      menu.forEach(x => {

        const sub = x.subCategories
          .filter(y => subcategories.map(z => z.id).includes(y.id));

        if (subcategoriesSon.length)
          sub
            .forEach(y => {
              y.subCategories = y.subCategories
                .filter(z => subcategoriesSon.map(x => x.id).includes(z.id))
            });

        x.subCategories = sub;
      });
    }

    const allMenu = menu.map<SidebarMenu>(x => this.#setUpMenuItem(x, x.id.toString()));

    this.#allSubcategoriesMenu.set(allMenu);
    this.sidebarMenu.set(allMenu);
  }

  handleCheckChange(item: SidebarCheckList, sublist: SidebarCheckList) {

    item.checked = item.sublist != null && item.sublist.every(t => t.checked);

    const filter: FilterTag = {
      name: sublist.name,
      sublistId: sublist.id,
      listId: item.id
    };

    if (sublist.checked) {
      item.countActiveFilters++;
      this.#addFilterTag(filter);
    }
    else {
      item.countActiveFilters--;
      this.#removeFilterTag(filter);
    }

    this.#addRemoveFilterConditions(item.key, sublist.id as number, !!sublist.checked);
    goToTopPage();
  }

  #addFilterTag(filter: FilterTag) {
    this.filterTags
      .update(value => {
        value.push(filter);
        return [...value];
      });
  }

  #removeFilterTag(filter: FilterTag) {
    this.filterTags
      .update(value => {

        const index = value
          .findIndex(x => x.listId === filter.listId &&
            x.sublistId === filter.sublistId);

        if (index !== -1)
          value.splice(index, 1);

        return [...value];
      });
  }

  #addRemoveFilterConditions(type: string, sublistId: number, isAdd: boolean) {

    this.sidebar()
      .filter(x => x.key !== type)
      .forEach(x => {

        if (x.key === type)
          return;

        this.#updateFilterMap(x.filters, type, sublistId, isAdd)
      });

    // Update the global filter
    const newMap = this.#updateFilterMap(
      structuredClone(this.#signalsStoreService.globalFilter().map),
      type,
      sublistId,
      isAdd
    );

    this.#signalsStoreService
      .globalFilter
      .set({ type, map: newMap ?? new Map<string, number[]>() });
  }

  #updateFilterMap(filters: Map<string, number[]>, type: string, id: number, isAdd: boolean) {

    type = type.split('-')[0];

    let condition = filters.get(type) ?? [];

    if (!isAdd && !condition?.length)
      return;

    if (!condition?.length)
      filters.set(type, [id]);
    else {

      if (isAdd)
        condition.push(id);
      else {
        const index = condition.findIndex(x => x === id);

        if (index === -1)
          return;

        condition.splice(index, 1);
      }

      // Update the map
      filters.set(type, condition);

      if (!condition.length)
        filters.delete(type);
    }

    return filters;
  }

  removeTag(filter: FilterTag) {

    const index = this.sidebar()
      .findIndex(x => x.id === filter.listId);

    const item = this.sidebar()[index];

    const sublist = item.sublist
      ?.find(x => x.id === filter.sublistId);

    if (sublist) {
      sublist.checked = false;
      this.handleCheckChange(item, sublist);
    }
  }

  getSubcategoryFiltered(products: ProductResume[]) {

    const categoriesId = products.map(x => [+x.category.id, +x.subcategory.id, x.subcategorySon?.id ?? 0]);

    const parent = structuredClone(this.#allSubcategoriesMenu()
      .filter(x => categoriesId.map(x => x[0]).includes(+x.id)));

    parent
      .forEach(x => {
        if (x.sublist?.length)
          x.sublist = x.sublist.filter(x => categoriesId.map(y => y[1]).includes(+x.id)) ?? [];

        x.sublist
          ?.forEach(y => {
            if (y.sublist?.length)
              y.sublist = y.sublist.filter(z => categoriesId.map(y => y[2]).includes(+z.id)) ?? [];
          })
      });

    this.sidebarMenu.set(parent);

    if (parent.length)
      this.#selectFirstSubcategory();
  }

  restoreFilters() {

    this.sidebar.update(current =>
      current.map(e => ({
        ...e,
        disabled: false,
        sublist: e.sublist?.map(x => ({
          ...x,
          disabled: false
        }))
      }))
    );

    this.sidebarMenu.set(structuredClone(this.#allSubcategoriesMenu()));
    if (this.sidebarMenu().length)
      this.#selectFirstSubcategory();
  }

  #selectFirstSubcategory() {

    let categoryId = 0, subCategoryId = 0, subCategorySonId = 0;

    const firstItem = this.sidebarMenu()[0];

    categoryId = +firstItem.id;

    if (firstItem.sublist?.length) {
      subCategoryId = +firstItem.sublist[0].id;

      if (firstItem.sublist[0].sublist?.length)
        subCategorySonId = +firstItem.sublist[0].sublist[0].id;
    }

    this.#signalsStoreService
      .categoryInViewPort
      .set({
        categoryId,
        subCategoryId,
        subCategorySonId
      });
  }

  #setUpMenuItem(item: Menu, key: string): SidebarMenu {

    let sublist: SidebarMenu[] = [];

    if (item.subCategories?.length)
      sublist = item.subCategories.map(x => this.#setUpMenuItem(x, `${key}-${x.id}`));

    return {
      disabled: false,
      checked: false,
      name: item.name,
      id: item.id,
      key,
      sublist
    };
  }

  clearFilter() {
    this.filterTags.set([]);
    this.#signalsStoreService.globalFilter.set({ type: '', map: new Map() });
  }

  //#endregion
}
